import { Box } from '@mui/material'
import { FunctionComponent, ReactNode } from 'react'
import { motion } from 'motion/react'
import { IGaiaReportWebpageReport } from '../shared-services/public-reports.model';
import { GaiaReportWebPageHero } from './gaia-report-wep-page-hero';
import { GaiaReportWebPageExecSummary } from './gaia-report-web-page-exec-summary';
import { GaiaReportWebPageMethodology } from './gaia-report-wep-page-methodology';
import { GaiaReportWebPageEmissions } from './gaia-report-web-page-emissions';
import { GaiaReportWebPageReduction } from './gaia-report-web-page-reduction';
import { GaiaReportWebPageOffsets } from './gaia-report-web-page-offsets';
import { buildGaiaReportColorPalette, getReportChartSeriesColors } from '../utils/gaia-report-color-palette';
import type { IColorPalatte } from '../shared-services/public-reports.model';
import { GaiaReportWebPageNetEmissions } from './gaia-report-web-page-net-emissions';
import { GaiaReportWebPageCompareEmissions } from './gaia-report-web-page-compare-emissions';
import { GaiaReportWebPageClosing } from './gaia-report-web-page-closing';
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode';
import { ReportContentProvider, ReportContentTokens } from '../shared-services/gaia-report-content';

export const GaiaReportWebPage: FunctionComponent<{ report: IGaiaReportWebpageReport }> = ({ report }) => {

  const pdfExport = usePdfExportMode();
  const organisationDetails = report.organisationDetails;

  const motionInitial = pdfExport ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 };
  const motionWhileInView = { opacity: 1, y: 0 };
  const motionTransition = pdfExport ? { duration: 0 } : { duration: 0.6, ease: 'easeOut' };

  const colorPalatte: IColorPalatte = {
    ...buildGaiaReportColorPalette(
      organisationDetails.organisationColor,
      organisationDetails.paletteOverrides ?? null
    ),
    chartSeries: getReportChartSeriesColors(
      organisationDetails.organisationColor,
      organisationDetails.paletteOverrides ?? null
    ),
  }

  const excludedSections = (report as any)?.excludedSections || [];
  const isSectionExcluded = (sectionName: string) => excludedSections.includes(sectionName);
  const isDirectCustomer = !organisationDetails.partnerName;

  const screenLabels: any = organisationDetails.screenLabels;
  const contentTokens: ReportContentTokens = {
    company: organisationDetails.organisationName,
    year: organisationDetails.reportPeriodYear,
    calendarYear: organisationDetails.reportPeriodCalendarYear,
    partner: organisationDetails.partnerName || 'Gaia',
    organisation: screenLabels?.organisation?.singular ?? 'organisation',
    locations: screenLabels?.location?.pluralCap ?? 'Locations',
    suppliers: screenLabels?.supplier?.pluralCap ?? 'Suppliers',
    products: screenLabels?.product?.pluralCap ?? 'Products',
    supplier: screenLabels?.supplier?.singular ?? 'supplier',
    product: screenLabels?.product?.singular ?? 'product',
  };

  const renderSection = (content: ReactNode, options?: { disableAnimation?: boolean }) => (
    <motion.div
      initial={options?.disableAnimation ? false : motionInitial}
      whileInView={motionWhileInView}
      viewport={{ once: true, margin: '-100px' }}
      transition={options?.disableAnimation ? { duration: 0 } : motionTransition}
      style={pdfExport ? { breakBefore: 'page' } : undefined}
    >
      {content}
    </motion.div>
  );

  return (
    <ReportContentProvider overrides={report.sectionContent} tokens={contentTokens}>
    <Box>
      <GaiaReportWebPageHero
        colorPalatte={colorPalatte}
        reportPeriodName={organisationDetails.reportPeriodName}
        organisationName={organisationDetails.organisationName}
        organisationLogoFilepath={organisationDetails.organisationLogoFilepath || undefined}
        coverImageFilepath={organisationDetails.coverImageFilepath}
        textColor={organisationDetails.textColor}
        customTitle={organisationDetails.customTitle}
        customReportPeriodName={organisationDetails.customReportPeriodName}
      />
      {!isSectionExcluded('executiveSummary') &&
        renderSection(
          <GaiaReportWebPageExecSummary
            colorPalatte={colorPalatte}
            partnerName={organisationDetails.partnerName}
            reportPeriodYear={organisationDetails.reportPeriodYear}
            companyName={organisationDetails.organisationName}
            reportPeriodCalendarYear={organisationDetails.reportPeriodCalendarYear}
          />,
          { disableAnimation: true }
        )}
      {!isSectionExcluded('methodology') &&
        renderSection(
          <GaiaReportWebPageMethodology
            screenLabels={organisationDetails.screenLabels}
            colorPalatte={colorPalatte}
            reportPeriodYear={organisationDetails.reportPeriodYear}
            reportPeriodCalendarYear={organisationDetails.reportPeriodCalendarYear}
          />,
          { disableAnimation: true }
        )}
      {!isSectionExcluded('emissionDetails') &&
        renderSection(
          <GaiaReportWebPageEmissions
            screenLabels={organisationDetails.screenLabels}
            colorPalatte={colorPalatte}
            companyName={organisationDetails.organisationName}
            emissionDetails={report.emissionDetails}
            reportPeriodCalendarYear={organisationDetails.reportPeriodCalendarYear}
            showSuppliersAndProducts={!isSectionExcluded('suppliersAndProducts')}
          />
        )}
      {!isSectionExcluded('reductionDetails') &&
        renderSection(
          <GaiaReportWebPageReduction
            colorPalatte={colorPalatte}
            companyName={organisationDetails.organisationName}
            reductionDetails={report.reductionDetails}
          />,
          { disableAnimation: true }
        )}
      {!isSectionExcluded('offsets') &&
        renderSection(
          <GaiaReportWebPageOffsets
            colorPalatte={colorPalatte}
            companyName={organisationDetails.organisationName}
            reductionDetails={report.reductionDetails.reductionOffsetsData}
          />
        )}
      {!isSectionExcluded('offsets') &&
        renderSection(
          <GaiaReportWebPageNetEmissions
            colorPalatte={colorPalatte}
            companyName={organisationDetails.organisationName}
            reductionDetails={report.reductionDetails.reductionOffsetsData}
          />,
          { disableAnimation: true }
        )}
      {!isSectionExcluded('emissionsOverTime') &&
        renderSection(
          <GaiaReportWebPageCompareEmissions
            colorPalatte={colorPalatte}
            companyName={organisationDetails.organisationName}
            comparisonDetails={report.comparisonDetails}
          />
        )}
      {!isSectionExcluded('closing') &&
        renderSection(
          <GaiaReportWebPageClosing
            darkColor={colorPalatte.dark}
            partnerLogoFilepath={organisationDetails.partnerLogoFilepath}
            isDirectCustomer={isDirectCustomer}
          />,
          { disableAnimation: true }
        )}
    </Box>
    </ReportContentProvider>
  )

}